import React, { useCallback, useMemo, useRef, useState } from 'react';
import { functionNoop } from 'src/utils/function/noop';
import * as Yup from 'yup';
import { FormikProps } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { DashboardSettingsAccountChangePasswordPanel } from './change-password-panel';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { JourneyFormik, JourneyForm } from 'src/common/JourneyFormik';
import { useCurrentUser } from 'src/store/user';
import { Nullable } from 'src/types/nullable.type';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import UserAvatar from 'src/common/components/user-avatar';
import { dataUrlToFile } from 'src/common/helpers/file';
import { uploadImageAssetToS3 } from 'src/common/helpers/file-upload';
import { apiDeleteOrganization, apiUpdateUser } from 'src/utils/journeyApi';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { EventBus } from 'src/common/EventBus';

import AnalyticsHelper from 'src/common/helpers/analytics';
import { AccountDeactiveWidget } from './deactivate/widget';
import { ErrorInterface } from 'src/common/interfaces/error.interface';

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  email: Yup.string().email().required('Required'),
  avatar_url: Yup.string(),
});

export const DashboardSettingsAccount = () => {
  const currentUser = useCurrentUser((state) => state.currentUser);
  const setCurrentUser = useCurrentUser((state) => state.setCurrentUser);
  const passwordUpdateOpenRef = useRef(functionNoop);
  const { setErrorNotification, setSuccessNotification } = useNotificationMessage();

  const [submitting, setSubmitting] = useState(false);
  const [avatarFile, setAvatarFile] = useState<Nullable<File>>(null);

  const onAvatarUpload = useCallback(async (cropSrc) => {
    if (!cropSrc) {
      return;
    }
    setAvatarFile(await dataUrlToFile(cropSrc!, `${uuidv4().replace(/-/g, '').substring(0, 6)}.png`));
  }, []);

  const formikParams = useMemo(() => {
    const user = currentUser as OrganizationUser;
    return {
      initialValues: { name: user.name, email: user.email },
      validationSchema,
      onSubmit: async (values: Record<string, string>) => {
        try {
          setSubmitting(true);
          if (avatarFile) {
            const response = await uploadImageAssetToS3(avatarFile);
            if (!response) throw new Error('Could not upload image');
            const { url } = response;
            values.avatar_url = url;
          }

          const updatedUser = await apiUpdateUser(values);
          if (updatedUser) {
            setSubmitting(false);
            setCurrentUser(updatedUser);
            setSuccessNotification('Account updated successfully');
            EventBus.emit('account-settings:user-updated', updatedUser);
            AnalyticsHelper.identifyUser(updatedUser);
          }
        } catch (error: any) {
          setSubmitting(false);
          setErrorNotification(error.message);
        }
      },
    };
  }, [currentUser, avatarFile, setCurrentUser, setErrorNotification, setSuccessNotification]);

  const onChangePasswordClicked = useCallback(() => {
    passwordUpdateOpenRef.current();
  }, []);

  const onPasswordUpdateSuccess = useCallback(
    (user: OrganizationUser) => {
      setCurrentUser(user);
    },
    [setCurrentUser]
  );

  const onAcceptDeactivateClicked = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    try {
      await apiDeleteOrganization(currentUser.last_organization.id);
    } catch (err) {
      setErrorNotification((err as ErrorInterface).message);
    }
  }, []);

  return (
    <>
      <JourneyFormik {...formikParams}>
        {({ values, initialValues }: FormikProps<Record<string, string>>) => {
          const isFormValid = validationSchema.isValidSync(values);

          const atLeastOneValueChanged =
            Object.entries(initialValues).some(([key, value]) => values[key] !== value) || !!avatarFile;

          return (
            <JourneyForm className='flex w-[320px] flex-col space-y-4 mt-6'>
              <FormikTextInput id='name' required name='name' type='text' label='Name' />
              <FormikTextInput id='email-address' required name='email' type='email' label='Email address' />
              <UserAvatar updateCrop={onAvatarUpload} />

              {currentUser?.has_password && (
                <div className='flex flex-col space-y-2'>
                  <span className='text-bedrock-p-strong text-bedrock-black'>Update password</span>
                  <button type='button' className='btn-bedrock-secondary self-start' onClick={onChangePasswordClicked}>
                    Change password
                  </button>
                </div>
              )}
              <div className='flex justify-start gap-2'>
                <button
                  type='submit'
                  className='btn-bedrock-primary'
                  disabled={submitting || !isFormValid || !atLeastOneValueChanged}
                >
                  Save
                </button>
                <AccountDeactiveWidget onAcceptClick={onAcceptDeactivateClicked} />
              </div>
            </JourneyForm>
          );
        }}
      </JourneyFormik>
      <DashboardSettingsAccountChangePasswordPanel
        openRef={passwordUpdateOpenRef}
        onSuccess={onPasswordUpdateSuccess}
      />
    </>
  );
};
